import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value = 0 }) => (
  <div className="flex gap-1" aria-label={`${value} / 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`w-4 h-4 ${n <= value ? "fill-[#C86D51] text-[#C86D51]" : "text-[#0D253A]/20"}`}
      />
    ))}
  </div>
);

const Reviews = () => {
  const { t } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/reviews`)
      .then(({ data }) => alive && setReviews(data.reviews || []))
      .catch(() => alive && setReviews([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const count = reviews.length;
  const current = count > 0 ? reviews[idx % count] : null;
  const next = () => setIdx((i) => (i + 1) % Math.max(count, 1));
  const prev = () => setIdx((i) => (i - 1 + count) % Math.max(count, 1));

  const avg = count ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / count) : 0;

  return (
    <section
      id="recensioni"
      data-testid="reviews-section"
      className="py-20 lg:py-32 px-6 sm:px-12 lg:px-20 bg-[#F8F5F0] relative"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <p className="text-xs uppercase tracking-[0.25em] font-mono-alt font-semibold text-[#C86D51] mb-4">
              {t.reviews.eyebrow}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0D253A]">
              {t.reviews.title}
            </h2>
            <p className="mt-5 text-base sm:text-lg text-[#0D253A]/70 leading-relaxed">
              {t.reviews.text}
            </p>

            {count > 0 && (
              <div className="mt-8 flex items-center gap-4">
                <Stars value={Math.round(avg)} />
                <span data-testid="reviews-average" className="font-display text-2xl text-[#0D253A]">
                  {avg.toFixed(1)}
                </span>
                <span className="text-sm text-[#0D253A]/60">
                  · {count} {count === 1 ? "review" : "reviews"}
                </span>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-7"
          >
            {loading ? (
              <div className="h-72 rounded-3xl bg-white/60 border border-[#0D253A]/10 animate-pulse" />
            ) : count === 0 ? (
              <p data-testid="reviews-empty" className="text-sm text-[#5C6773] italic">
                {t.reviews.empty}
              </p>
            ) : (
              <div
                className="relative bg-white rounded-3xl border border-[#0D253A]/10 p-8 sm:p-10 shadow-[0_30px_70px_-40px_rgba(13,37,58,0.35)] overflow-hidden"
                data-testid="reviews-carousel"
              >
                <Quote className="absolute top-6 right-6 w-16 h-16 text-[#C86D51]/10" />
                <AnimatePresence mode="wait">
                  <motion.div
                    key={current.id}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.5 }}
                    data-testid={`review-card-${current.id.slice(0, 8)}`}
                  >
                    <Stars value={current.rating} />
                    {current.title && (
                      <h3 className="mt-5 font-display text-2xl text-[#0D253A] leading-snug">
                        {current.title}
                      </h3>
                    )}
                    <p className="mt-4 text-base text-[#333D47] leading-relaxed">"{current.text}"</p>
                    <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                      <div>
                        <p className="font-semibold text-[#0D253A]">{current.guest_name}</p>
                        <p className="text-xs text-[#0D253A]/60">
                          {[current.guest_city, current.stay_period].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                      {count > 1 && (
                        <div className="flex items-center gap-2">
                          <button
                            data-testid="reviews-prev"
                            onClick={prev}
                            aria-label={t.reviews.prev}
                            className="p-2.5 rounded-full border border-[#0D253A]/15 text-[#0D253A]/70 hover:text-[#C86D51] hover:border-[#C86D51] transition-colors"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          <span className="text-xs font-mono-alt text-[#0D253A]/60">
                            {idx + 1} / {count}
                          </span>
                          <button
                            data-testid="reviews-next"
                            onClick={next}
                            aria-label={t.reviews.next}
                            className="p-2.5 rounded-full border border-[#0D253A]/15 text-[#0D253A]/70 hover:text-[#C86D51] hover:border-[#C86D51] transition-colors"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
